import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { ImageData } from "../../../store/types/strapi-types";

const baseUrl =
  process.env.REACT_APP_STRAPI_IMAGES_URL?.replace(/\/$/, "") || "";

interface Props {
  videoUrl?: string;
  images: null | ImageData[];
  floorPlans: null | ImageData[];
}

const getThumbnailUrl = (img: ImageData) =>
  img.attributes.formats?.small?.url
    ? baseUrl + img.attributes.formats.small.url
    : baseUrl + img.attributes.url;

const getOriginalUrl = (img: ImageData) => baseUrl + img.attributes.url;

const MobileProjectGallery = ({ videoUrl, images, floorPlans }: Props) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
  const thumbnailsRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [fullscreenMedia, setFullscreenMedia] = useState<string | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const thumbnails = [
    ...(videoUrl ? [videoUrl] : []),
    ...(images?.map(getThumbnailUrl) || []),
    ...(floorPlans?.map(getThumbnailUrl) || []),
  ];

  const originalMedia = [
    ...(videoUrl ? [videoUrl] : []),
    ...(images?.map(getOriginalUrl) || []),
    ...(floorPlans?.map(getOriginalUrl) || []),
  ];

  const changeMedia = (index: number) => {
    setSelectedIndex(index);
    scrollToThumbnail(index);
  };

  const scrollToThumbnail = (index: number) => {
    if (thumbnailsRef.current) {
      const thumbnails = thumbnailsRef.current.children;
      if (thumbnails[index]) {
        (thumbnails[index] as HTMLElement).scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    }
  };

  useEffect(() => {
    scrollToThumbnail(selectedIndex);
  }, [selectedIndex]);

  return (
    <div className="w-full flex flex-col items-center mb-[7rem]">
      {originalMedia[selectedIndex] && (
        <motion.div
          key={originalMedia[selectedIndex]}
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="relative w-full h-[27rem] cursor-pointer overflow-hidden rounded-lg mb-6"
          onClick={() => {
            if (!originalMedia[selectedIndex].includes("vimeo")) {
              setFullscreenMedia(originalMedia[selectedIndex]);
            }
          }}
        >
          {originalMedia[selectedIndex].includes("vimeo") ? (
            <div className="relative w-full h-full">
              <iframe
                className="w-full h-full rounded-lg"
                style={{ borderRadius: "0.5rem" }}
                src={`https://player.vimeo.com/video/${getVimeoId(
                  originalMedia[selectedIndex]
                )}`}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="vimeo video"
                onLoad={() => setIsVideoPlaying(true)}
              />

              {!isVideoPlaying && (
                <div
                  className="absolute inset-0 flex justify-center items-center bg-black bg-opacity-40"
                  onClick={() => setIsVideoPlaying(true)}
                >
                  <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <div
                      className="w-0 h-0 border-l-[12px] border-t-[8px] border-b-[8px] border-transparent border-l-black"
                      style={{ marginLeft: "3px" }}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <img
              src={originalMedia[selectedIndex]}
              alt="Project media"
              className="w-full h-full object-contain rounded-lg"
            />
          )}
        </motion.div>
      )}

      {thumbnails.length > 1 && (
        <div className="w-full overflow-hidden">
          <div
            ref={thumbnailsRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory"
            style={{
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
              display: "flex",
              width: "100%",
            }}
          >
            {thumbnails.map((media, index) => (
              <motion.div
                key={index}
                className={`w-[15rem] h-[12rem] cursor-pointer rounded-lg overflow-hidden flex-shrink-0 ${
                  selectedIndex === index ? "border-2 border-blue-500" : ""
                }`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                onClick={() => changeMedia(index)}
              >
                {media.includes("vimeo") ? (
                  <div className="relative w-full h-full">
                    <iframe
                      className="w-full h-full object-cover rounded-md pointer-events-none"
                      src={`https://player.vimeo.com/video/${getVimeoId(
                        media
                      )}?background=1&autoplay=0&muted=1&playsinline=1&controls=0`}
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title="Vimeo Thumbnail Preview"
                    />
                    <div className="absolute inset-0 flex justify-center items-center">
                      <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg">
                        <div
                          className="w-0 h-0 border-l-[12px] border-t-[8px] border-b-[8px] border-transparent border-l-black"
                          style={{ marginLeft: "3px" }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <img
                    src={media}
                    alt="Project Thumbnail"
                    className="w-full h-full object-cover"
                  />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {fullscreenMedia && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-90 flex justify-center items-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setFullscreenMedia(null)}
        >
          <div
            className="absolute top-5 right-5 text-white text-3xl cursor-pointer bg-gray-800 px-3 py-2 rounded-full"
            onClick={() => setFullscreenMedia(null)}
          >
            ✖
          </div>
          <img
            src={fullscreenMedia}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            alt="Fullscreen"
          />
        </motion.div>
      )}
    </div>
  );
};

const getVimeoId = (url: string): string => {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : "";
};

export default MobileProjectGallery;
